import { Card, CardContent, CardHeader, CardTitle, Button } from "@learn-me/ui";
import { CheckCircle, X, Save, Trash2, HelpCircle } from "lucide-react";
import type { GeneratedQuiz } from "../api/adminApi";

interface QuizPreviewProps {
  quiz: GeneratedQuiz;
  onSave: () => void;
  onDiscard: () => void;
  isSaving: boolean;
}

export function QuizPreview({
  quiz,
  onSave,
  onDiscard,
  isSaving,
}: QuizPreviewProps) {
  return (
    <Card className="border-green-200 bg-gradient-to-r from-green-50 to-emerald-50">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2 text-green-800">
            <HelpCircle className="h-5 w-5" />
            Quiz Generado: {quiz.title}
          </CardTitle>
          <span className="text-sm text-green-600 bg-green-100 px-2 py-1 rounded">
            {quiz.questions.length} preguntas
          </span>
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        <p className="text-sm text-green-700">
          Revisa las preguntas generadas antes de guardar el quiz.
        </p>

        {/* Questions List */}
        <div className="space-y-4 max-h-[400px] overflow-y-auto pr-2">
          {quiz.questions.map((question, qIdx) => (
            <div
              key={qIdx}
              className="p-4 bg-white rounded-lg border border-green-200"
            >
              <div className="flex items-start gap-3">
                <span className="flex-shrink-0 w-6 h-6 rounded-full bg-green-100 text-green-700 text-sm font-medium flex items-center justify-center">
                  {qIdx + 1}
                </span>
                <div className="flex-1 space-y-3">
                  <p className="font-medium text-gray-900">{question.text}</p>

                  {/* Options */}
                  <div className="space-y-2">
                    {question.options.map((option, oIdx) => (
                      <div
                        key={oIdx}
                        className={`flex items-center gap-2 p-2 rounded text-sm ${
                          option.isCorrect
                            ? "bg-green-100 text-green-800 border border-green-300"
                            : "bg-gray-50 text-gray-700"
                        }`}
                      >
                        {option.isCorrect ? (
                          <CheckCircle className="h-4 w-4 text-green-600 flex-shrink-0" />
                        ) : (
                          <X className="h-4 w-4 text-gray-400 flex-shrink-0" />
                        )}
                        <span>{option.text}</span>
                        {option.isCorrect && (
                          <span className="ml-auto text-xs font-medium text-green-600">
                            Correcta
                          </span>
                        )}
                      </div>
                    ))}
                  </div>

                  {/* Explanation */}
                  {question.explanation && (
                    <div className="pt-2 border-t border-gray-100">
                      <p className="text-xs text-gray-500">
                        <span className="font-medium">Explicación: </span>
                        {question.explanation}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Action Buttons */}
        <div className="flex gap-3 pt-2">
          <Button
            onClick={onSave}
            disabled={isSaving}
            className="flex-1 bg-green-600 hover:bg-green-700"
          >
            <Save className="h-4 w-4 mr-2" />
            {isSaving ? "Guardando..." : "Guardar Quiz"}
          </Button>
          <Button
            variant="outline"
            onClick={onDiscard}
            disabled={isSaving}
            className="border-red-300 text-red-600 hover:bg-red-50"
          >
            <Trash2 className="h-4 w-4 mr-2" />
            Descartar
          </Button>
        </div>

        <p className="text-xs text-green-600 text-center">
          Al guardar, este quiz se asociará al capítulo actual.
        </p>
      </CardContent>
    </Card>
  );
}
